import {
  Dog, Users, Wifi, Car, ShoppingBag, Trees, Gift, Accessibility, Coffee, Baby, Beer,
  UtensilsCrossed, ShieldCheck, BookOpen, Cake, Leaf, PartyPopper, Clock
} from 'lucide-react';

export const ICONS = { Dog, Users, Wifi, Car, ShoppingBag, Trees, Gift, Accessibility, Coffee, Baby, Beer, UtensilsCrossed, ShieldCheck, BookOpen, Cake, Leaf, PartyPopper, Clock };

export const FONTS = [
  { value: 'Georgia, serif', label: 'Classic Serif' },
  { value: '"Times New Roman", serif', label: 'Times' },
  { value: 'Inter, sans-serif', label: 'Inter (Modern)' },
  { value: 'system-ui, sans-serif', label: 'System Sans' },
  { value: '"Great Vibes", cursive', label: 'Script' },
];

export const DEFAULT_THEME = {
  heading_font: 'Georgia, serif',
  body_font: 'Inter, sans-serif',
  primary_color: '#1A2744',
  accent_color: '#C9A84C',
};

export const SECTION_LABELS = {
  hero: 'Hero Banner', quick_links: 'Quick Links', about: 'About Us & Facilities',
  news: 'News', gift_card: 'Gift Cards', instagram: 'Instagram',
};

export const DEFAULT_SECTIONS = [
  { id: 'hero', type: 'hero', visible: true, settings: {
    heading: 'Peony Gifts & Kitchen', subheading: 'Peony Hospitality Ltd', button_text: 'View Our Menu',
    button_link: '/Menus', image_url: '', text_color: '#FFFFFF' } },
  { id: 'quick_links', type: 'quick_links', visible: true, settings: { bg_color: '#FFFFFF', items: [
    { title: 'Allergens', text: 'Full allergen information for every dish.', link: '/Allergens', icon: 'ShieldCheck' },
    { title: 'Order Online', text: 'Collection and delivery, made easy.', link: '/Order', icon: 'ShoppingBag' },
    { title: 'Our Menus', text: 'Seasonal dishes, brunch and drinks.', link: '/Menus', icon: 'BookOpen' },
    { title: 'Reserve a Table', text: 'Secure your table in moments.', link: '/BookATable', icon: 'UtensilsCrossed' },
  ] } },
  { id: 'about', type: 'about', visible: true, settings: {
    bg_color: '#F7F3E6', title: 'About Us',
    description: 'Peony Hospitality Ltd has multiple food and drink venues across Derbyshire, where we create a warm, welcoming space to enjoy freshly prepared meals, artisan coffee and homemade treats.',
    hours_title: 'Opening Times', button_text: 'Contact Us', button_link: '/ContactUs',
    hours: [
      { day: 'Monday - Friday', time: '9:00am - 4:00pm' },
      { day: 'Saturday', time: '9:00am - 4:00pm' },
      { day: 'Sunday', time: '10:00am - 3:00pm' },
    ],
    facilities_title: 'Facilities',
    facilities: [
      { label: 'Dog Friendly', icon: 'Dog' }, { label: 'Family Friendly', icon: 'Users' },
      { label: 'Free WiFi', icon: 'Wifi' }, { label: 'Car Park', icon: 'Car' },
      { label: 'Takeaway', icon: 'ShoppingBag' }, { label: 'Gift Shop', icon: 'Gift' },
      { label: 'Accessible', icon: 'Accessibility' }, { label: 'Artisan Coffee', icon: 'Coffee' },
    ] } },
  { id: 'news', type: 'news', visible: true, settings: {
    bg_color: '#FFFFFF', title: 'News', subtitle: '' } },
  { id: 'gift_card', type: 'gift_card', visible: true, settings: {
    bg_color: '#F7F3E6', title: 'Why not spoil the ones you love with a Peony Gift Card?',
    text: 'Give the gift of a relaxed brunch, a catch up over coffee or a sweet treat, with a Peony e-gift card delivered instantly.',
    button_text: 'Buy a Gift Card', button_link: '/BuyGiftCard',
    button_2_text: 'Check a Balance', button_2_link: '/CheckGiftCardBalance',
    image_url: 'https://images.unsplash.com/photo-1513201099705-a9746e1e201f?w=1200&q=80' } },
  { id: 'instagram', type: 'instagram', visible: true, settings: {
    title: 'Explore our Instagram', subtitle: 'Click any image below to view our Instagram post.' } },
];

const t = (key, label, type = 'text', extra = {}) => ({ key, label, type, ...extra });

export const SECTION_FIELDS = {
  hero: [t('heading', 'Heading'), t('subheading', 'Subheading'), t('button_text', 'Button text'),
    t('button_link', 'Button link', 'link'), t('image_url', 'Background image (blank = Hero Section image)', 'image'),
    t('text_color', 'Text colour', 'color')],
  quick_links: [t('bg_color', 'Background colour', 'color'), t('text_color', 'Text colour', 'color'), t('items', 'Links', 'list', { itemFields: [
    t('title', 'Title'), t('text', 'Text'), t('link', 'Link', 'link'), t('icon', 'Icon', 'icon')] })],
  about: [t('bg_color', 'Background colour', 'color'), t('text_color', 'Text colour', 'color'), t('title', 'Title'), t('description', 'Description', 'textarea'),
    t('hours_title', 'Opening times title'), t('hours', 'Opening times', 'list', { itemFields: [t('day', 'Day'), t('time', 'Hours')] }),
    t('button_text', 'Button text'), t('button_link', 'Button link', 'link'),
    t('facilities_title', 'Facilities title'), t('facilities', 'Facilities', 'list', { itemFields: [t('label', 'Name'), t('icon', 'Icon', 'icon')] })],
  news: [t('bg_color', 'Background colour', 'color'), t('text_color', 'Text colour', 'color'), t('title', 'Title'), t('subtitle', 'Subtitle')],
  gift_card: [t('bg_color', 'Panel colour', 'color'), t('text_color', 'Text colour', 'color'), t('title', 'Title'), t('text', 'Text', 'textarea'),
    t('button_text', 'Button text'), t('button_link', 'Button link', 'link'),
    t('button_2_text', 'Second button text'), t('button_2_link', 'Second button link', 'link'), t('image_url', 'Image', 'image')],
  instagram: [t('text_color', 'Text colour', 'color'), t('title', 'Title'), t('subtitle', 'Subtitle')],
};

// ── App Home (native app / Base44 domain) ──
const byType = (type) => DEFAULT_SECTIONS.find((s) => s.type === type);
export const APP_DEFAULT_SECTIONS = [
  { id: 'news', type: 'news', visible: true, settings: { bg_color: '#FFFFFF', title: '', subtitle: '' } },
  { id: 'instagram', type: 'instagram', visible: true, settings: { ...byType('instagram').settings } },
  // Extra sections — hidden until switched on in the App Home Builder.
  ...['quick_links', 'about', 'gift_card'].map((type) => ({ ...byType(type), visible: false })),
];

export function mergeLayout(record, defaultSections = DEFAULT_SECTIONS) {
  const defaults = Object.fromEntries(defaultSections.map((s) => [s.type, s]));
  const saved = (record?.sections || []).filter((s) => defaults[s.type]);
  const sections = saved.map((s) => ({ ...defaults[s.type], ...s, settings: { ...defaults[s.type].settings, ...s.settings } }));
  defaultSections.forEach((d) => { if (!sections.some((s) => s.type === d.type)) sections.push(d); });
  return { theme: { ...DEFAULT_THEME, ...record?.theme }, sections };
}

export function followLink(link, navigate) {
  if (!link) return;
  if (/^https?:\/\//.test(link)) window.location.href = link;
  else navigate(link.startsWith('/') ? link : `/${link}`);
}